import React from "react";
import styled from "styled-components";
import Colors from "../../../ui/global/Colors";
import Button from "../../../ui/components/Button";
import Fonts from "../../../ui/global/Fonts";
import { HistoryEntry as HistoryEntryProps } from "../utils/useArtHistory";
import Tooltip from "../../../ui/components/Tooltip";

const ToolbarContainer = styled.div`
    width: 100%;
    min-height: 100%;
    display: flex;
    position: relative;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    overflow: visible;
`;

const HistoryEntry = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    gap: 2px;
`;

const ImagesInner = styled.div<{ $isSelected?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: space-between;
    justify-content: flex-end;
    background-color: ${Colors.transparent};
    border-radius: 2px;
    border: ${props => props.$isSelected ? 
        `1px solid ${Colors.black}` : 
        `1px solid ${Colors.transparent}`};
    
    object-fit: contain;
    cursor: pointer;
    z-index: 12;
    transition: all 0.2s ease-in-out;
    transform-origin: center bottom;

    &:hover {
        transform: scale(1.2);
        z-index: 2;
    }

    &:active {
        border: 1px solid ${Colors.black};
    }
`;

const Image = styled.img`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 2px;
    border: 1px solid ${Colors.transparent};
    border-left: 1px solid ${Colors.grayPale};
    border-right: 1px solid ${Colors.grayPale};
    object-fit: contain;
    margin: auto;
`;

const BlankImage = styled.div`
    width: 22px;
    height: 22px;
    background-color: ${Colors.white};
    border-radius: 2px;
    border: 1px solid ${Colors.transparent};
    border-left: 1px solid ${Colors.grayPale};
    border-right: 1px solid ${Colors.grayPale};
    margin: auto;
`;

const Blank = styled.div`
    width: 22px;
    height: 22px;
    background-color: ${Colors.transparent};
`;

const Description = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 14.4px;
    max-width: 100%;
    font-size: 12px;
    line-height: 1.2em;
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    font-family: ${Fonts.lexendSemiLight.fontFamily};
    color: ${Colors.grey6};
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

const SideContainer = styled.div`
    display: flex;
    flex-direction: row;
    height: 100%;
    border-radius: 4px;
    margin: 0;
    overflow: visible;
`;

const Left = styled(SideContainer)`
    position: sticky;
    margin: 0;
    left: 0;
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-start;
    
    > * {
        background-color: ${Colors.white};
        border-radius: 4px;
    }
`;

const Middle = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-start;
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    overflow-y: visible;
    scrollbar-width: thin;
    
    &::-webkit-scrollbar {
        height: 4px;
    }
    &::-webkit-scrollbar-track {
        background: ${Colors.transparent};
    }
    &::-webkit-scrollbar-thumb {
        background: ${Colors.grey1};
        border-radius: 4px;
    }
`;

const Right = styled(SideContainer)`
    position: sticky;
    right: 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    margin: auto 0;
    
    > * {
        // background-color: ${Colors.white};
        border-radius: 4px;
    }
`;

interface ToolbarHistoryProps {
    history: HistoryEntryProps[];
    currentIndex: number;
    onSelectState: ( state: HistoryEntryProps ) => void;
    onStartOver: () => void;
    config: any;
}

const ToolbarHistory: React.FC<ToolbarHistoryProps> = ({ history, currentIndex, onSelectState, onStartOver, config }) => {
    // Handle selecting a history entry
    const handleSelectEntry = (entry: HistoryEntryProps, index: number) => {
        onSelectState(entry);
    };

    // Handle undo
    const handleUndo = () => {
        if (currentIndex > 0) {
            const newIndex = currentIndex - 1;
            onSelectState(history[newIndex]);
        } else if (currentIndex === -1 && history.length > 0) {
            // If no history item is selected, select the most recent one
            onSelectState(history[history.length - 1]);
        }
    };

    // Handle redo
    const handleRedo = () => {
        if (currentIndex < history.length - 1) {
            const newIndex = currentIndex + 1;
            onSelectState(history[newIndex]);
        }
    };

    // Handle play (go to latest)
    const handlePlay = () => {
        if (history.length > 0) {
            onSelectState(history[history.length - 1]);
        }
    };

    // Handle clear history
    const handleClear = () => {
        onStartOver();
    };

    return (
        <ToolbarContainer>
            {/* <Left>
                <Button 
                    size={'small'} 
                    icon={'play'} 
                    color={'ghost'} 
                    onClick={handlePlay}
                    disabled={history.length === 0}/>
            </Left> */}
            <Middle>
                {history.map((entry, index) => (
                    // console.log(entry),
                    (config.SHOW_DRAW_HISTORY_USER_IMAGE) && (
                        <HistoryEntry id="history-entry"
                            key={entry.iterationId} 
                            onClick={() => handleSelectEntry(entry, index)}
                        >
                            <ImagesInner $isSelected={index === currentIndex}>
                                {config.SHOW_DRAW_HISTORY_USER_IMAGE && (
                                        entry.userPng ? (
                                            <Image
                                                key={entry.createdAt+'userPng'}
                                                src={entry.userPng}
                                                title={`v${index + 1}`}
                                            />
                                        ) : (
                                            <BlankImage key={entry.createdAt+'userPng'} />
                                        )
                                )}
                                {entry.aiImage ? (
                                    <Image
                                        key={entry.createdAt+'aiImage'}
                                        src={entry.aiImage}
                                        title={`v${index + 1}`}
                                    />
                                ) : (
                                    <Blank key={entry.createdAt+'aiImage'} />
                                )}
                            </ImagesInner>
                            {config.SHOW_DRAW_HISTORY_DESCRIPTION && (
                                <Description key={entry.createdAt+'description'}>
                                    {entry.description}
                                </Description>
                            )}
                        </HistoryEntry>
                    )
                ))}
            </Middle>
            <Right>
                <Tooltip text="Undo" colorMode="dark">
                    <Button 
                        size={'tiny'} 
                        icon={'undo'} 
                        color={'ghost'} 
                        onClick={handleUndo}
                        disabled={currentIndex <= 0 || history.length === 0}
                    />
                </Tooltip>
                <Tooltip text="Redo" colorMode="dark">
                    <Button 
                        size={'tiny'} 
                        icon={'redo'} 
                        color={'ghost'} 
                        onClick={handleRedo}
                        disabled={currentIndex >= history.length - 1 || history.length === 0}
                    />
                </Tooltip>
                <Tooltip text="Delete" colorMode="dark">
                    <Button 
                        size={'tiny'} 
                        icon={'trash-outline'} 
                        color={'ghost'} 
                        onClick={handleClear}
                        disabled={history.length === 0 || (history[currentIndex].aiImage === '' && history[currentIndex].userPng === '')}
                    />
                </Tooltip>
            </Right>
        </ToolbarContainer>
    );
};

export default ToolbarHistory;